import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  FileText,
  Users,
  CheckSquare,
  MessageSquare,
  Building,
  MapPin,
  DollarSign,
  Settings,
  Search,
  ChevronRight,
  X,
  ArrowLeft
} from 'lucide-react';
import { RefConfig, referenceFieldTemplates } from './ReferenceFieldTemplates';
import { cn } from '@/lib/utils';

interface ReferenceFieldTemplateSelectorProps {
  onSelectTemplate: (templateKey: string, config: RefConfig) => void;
  onCustomConfig: () => void;
  onCancel: () => void;
  currentConfig?: RefConfig | null;
}

// Category icons for the category pills
const categoryIcons: Record<string, React.ReactNode> = {
  'Documents': <FileText className="h-3.5 w-3.5" />,
  'Contacts': <Users className="h-3.5 w-3.5" />,
  'Tasks': <CheckSquare className="h-3.5 w-3.5" />,
  'Communication': <MessageSquare className="h-3.5 w-3.5" />,
  'Project Info': <MapPin className="h-3.5 w-3.5" />,
  'Team': <Users className="h-3.5 w-3.5" />,
  'Checklists': <CheckSquare className="h-3.5 w-3.5" />,
  'Partner Info': <Building className="h-3.5 w-3.5" />,
};

export function ReferenceFieldTemplateSelector({
  onSelectTemplate,
  onCustomConfig,
  onCancel,
  currentConfig
}: ReferenceFieldTemplateSelectorProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Get unique categories
  const categories = ["all", ...Array.from(new Set(Object.values(referenceFieldTemplates).map(t => t.category)))];

  // Filter templates based on category and search
  const filteredTemplates = Object.entries(referenceFieldTemplates).filter(([key, template]) => {
    const matchesCategory = selectedCategory === "all" || template.category === selectedCategory;
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Group templates by category for better organization
  const templatesByCategory = filteredTemplates.reduce((acc, [key, template]) => {
    if (!acc[template.category]) {
      acc[template.category] = [];
    }
    acc[template.category].push({ key, template });
    return acc;
  }, {} as Record<string, Array<{ key: string; template: typeof referenceFieldTemplates[string] }>>);

  return (
    <div className="space-y-4">
      {/* Header with back button */}
      <div className="flex items-center justify-between pb-3 border-b">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="h-8 px-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h3 className="font-semibold text-base">Select Reference Template</h3>
            <p className="text-xs text-muted-foreground">
              Choose a pre-built template or create custom
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onCustomConfig}
          className="h-8"
        >
          <Settings className="h-3.5 w-3.5 mr-1.5" />
          Custom
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          type="text"
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-8 h-9"
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
            onClick={() => setSearchQuery("")}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Category Pills */}
      <div className="flex flex-wrap gap-1.5">
        {categories.map(category => (
          <Button
            key={category}
            variant={selectedCategory === category ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory(category)}
            className={cn(
              "h-7 text-xs px-2.5 rounded-full",
              selectedCategory === category
                ? "bg-primary text-primary-foreground"
                : "hover:bg-muted"
            )}
          >
            {category !== "all" && categoryIcons[category] && (
              <span className="mr-1">{categoryIcons[category]}</span>
            )}
            {category === "all" ? "All" : category}
          </Button>
        ))}
      </div>

      {/* Template List */}
      <ScrollArea className="h-[320px] pr-3">
        {selectedCategory === "all" ? (
          // Show grouped by category when "All" is selected
          <div className="space-y-4">
            {Object.entries(templatesByCategory).map(([category, templates]) => (
              <div key={category}>
                <div className="flex items-center gap-2 mb-2 sticky top-0 bg-background py-1">
                  {categoryIcons[category]}
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {category}
                  </span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                <div className="space-y-1.5">
                  {templates.map(({ key, template }) => (
                    <TemplateItem
                      key={key}
                      templateKey={key}
                      template={template}
                      onSelect={onSelectTemplate}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Show flat list when a specific category is selected
          <div className="space-y-1.5">
            {filteredTemplates.map(([key, template]) => (
              <TemplateItem
                key={key}
                templateKey={key}
                template={template}
                onSelect={onSelectTemplate}
              />
            ))}
          </div>
        )}

        {filteredTemplates.length === 0 && (
          <div className="text-center py-8">
            <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm font-medium text-foreground mb-1">No templates found</p>
            <p className="text-xs text-muted-foreground">
              Try adjusting your search or filters
            </p>
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

// Individual template item component
function TemplateItem({
  templateKey,
  template,
  onSelect
}: {
  templateKey: string;
  template: typeof referenceFieldTemplates[string];
  onSelect: (key: string, config: RefConfig) => void;
}) {
  return (
    <button
      onClick={() => onSelect(templateKey, template.config)}
      className={cn(
        "w-full text-left p-3 rounded-lg border transition-all",
        "hover:bg-accent hover:border-accent-foreground/20",
        "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1",
        "group"
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 p-1.5 rounded-md bg-primary/10 text-primary">
          {template.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-sm text-foreground truncate">
              {template.name}
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {template.description}
          </p>
          {template.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {template.tags.slice(0, 3).map(tag => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="text-[10px] px-1.5 py-0 h-4 font-normal"
                >
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
